import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Text from '../../../components/Text/Text';
import Input from '../../../components/Input/Input';
import postLogin from '../../../thunks/auth/login';
import Alert from '../../../components/Alert/Alert';
import alertType from '../../../common/alertType';
import Button from '../../../components/Button/Button';
import routes from '../../../router/routes';
import { METHOD_POST } from '../../../constants/api';
import { set } from '../../../utils/request';
import getMyProfile from '../../../thunks/profile/getMyProfile';
import FaceLoginModal from '../../../components/FaceLoginModal/FaceLoginModal';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = JSON.parse(localStorage.getItem('token')) || {};
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [isVerifyingTwoFactor, setIsVerifyingTwoFactor] = useState(false);
  const [isFaceLoginVisible, setIsFaceLoginVisible] = useState(false);
  const {
    login: {
      loading,
      error: loginError,
    } = {}
  } = useSelector((state) => ({
    login: state.login
  }), shallowEqual);

  useEffect(() => {
    if (token) {
      navigate(routes.CO_ORDERS);
    }
  }, [token]);

  const {
    values: {
      email,
      password
    },
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    isValid,
    touched
  } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: async (values) => {
      setTwoFactorError(null);

      try {
        const response = await dispatch(postLogin(values)).unwrap();

        if (response?.requires_2fa) {
          setTwoFactorChallenge(response.challenge_token);
          setTwoFactorCode('');
        }
      } catch (error) {
        setTwoFactorChallenge(null);
      }
    },
    validationSchema: Yup.object().shape({
      password: Yup.string().required('Required'),
      email: Yup.string().required('Required'),
    })
  });

  const verifyTwoFactor = async () => {
    setIsVerifyingTwoFactor(true);
    setTwoFactorError(null);

    try {
      const response = await set(METHOD_POST, '/2fa/verify-login', {
        challenge_token: twoFactorChallenge,
        code: twoFactorCode
      });

      localStorage.setItem('token', JSON.stringify({ token: response.token }));
      await dispatch(getMyProfile());
      navigate(routes.CO_ORDERS);
    } catch (error) {
      setTwoFactorError(error.message);
    } finally {
      setIsVerifyingTwoFactor(false);
    }
  };

  return (
    <div className="bg-background-light min-h-screen flex justify-center items-center">
      <div className="w-full max-w-[450px] text-center p-[40px] rounded-[10px] shadowDefault">
        <Text
          text="Sign In To Admin"
          bold
          styleContainer="justify-center"
          textStyle="text-[20px]"
        />
        <Text
          muted
          text={twoFactorChallenge
            ? 'Enter the 6-digit code from Google Authenticator or a recovery code:'
            : 'Enter your details to login to your account:'}
          styleContainer="justify-center"
        />
        <div className="mb-[65px]" />
        <div>
          {!twoFactorChallenge ? (
            <>
              <div className="flex flex-col gap-3">
                <Input
                  type="text"
                  name="email"
                  value={email}
                  placeholder="login or email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  errorText={touched?.email && errors?.email}
                />
                <Input
                  type="password"
                  name="password"
                  value={password}
                  onBlur={handleBlur}
                  placeholder="Password"
                  onChange={handleChange}
                  errorText={touched?.password && errors?.password}
                />
              </div>
              <div className="flex flex-row justify-between mt-[20px] mb-[20px]">
                <a
                  href={routes.FORGOT_PASSWORD}
                  className="text-[14px] font-semibold text-primary no-underline hover:text-primary-hover"
                >
                  Forgot password?
                </a>
                <a
                  href={routes.REGISTRATION}
                  className="text-[14px] font-semibold text-primary no-underline hover:text-primary-hover"
                >
                  Create an Account
                </a>
              </div>
              <div className="flex flex-row justify-between">
                <Button
                  text="Login with Face"
                  disabled={loading}
                  onClick={() => setIsFaceLoginVisible(true)}
                />
                <Button
                  text="Sign In"
                  disabled={!isValid || loading}
                  onClick={handleSubmit}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                <Input
                  type="text"
                  name="twoFactorCode"
                  value={twoFactorCode}
                  placeholder="recovery code"
                  onChange={({ target }) => setTwoFactorCode(target.value)}
                  autoFocus
                />
              </div>
              <div className="flex flex-row justify-between">
                <button
                  type="button"
                  className="text-[14px] font-semibold text-primary border-none bg-transparent px-0 cursor-pointer"
                  onClick={() => {
                    setTwoFactorChallenge(null);
                    setTwoFactorCode('');
                    setTwoFactorError(null);
                  }}
                >
                  Back to login
                </button>
              </div>
              <div className="flex flex-row justify-center mb-[20px]">
                <Button
                  text="Verify Code"
                  disabled={!twoFactorCode || isVerifyingTwoFactor}
                  onClick={verifyTwoFactor}
                />
              </div>
            </>
          )}
          {(loginError || twoFactorError) && (
            <Alert
              text={loginError || twoFactorError}
              type={alertType.warning}
            />
          )}
        </div>
      </div>
      <FaceLoginModal
        visible={isFaceLoginVisible}
        onClose={() => setIsFaceLoginVisible(false)}
        onAuthenticated={() => navigate(routes.CO_ORDERS)}
      />
    </div>
  );
};

export default Login;
